import {
    assertIsArray,
    assertIsNonEmptyString,
    assertIsNotNull,
    assertIsNumber,
    assertIsObject,
    assertIsString,
    assertIsTrue,
    assertIsUUID4,
} from '../../lib/test-and-assert/assert-base';
import { assertIsRawJobInfo } from '../uploader/core/assert';
import {
    testIsFunction,
    testIsNonEmptyString,
    testIsNumber,
    testIsObject,
    testIsUndefined,
} from '../../lib/test-and-assert/test-base';

import { globalLogger } from '../../qaamgo/helper/global-logger';

import { SatApiHelper } from './sat-api-helper';

import { AjaxWrapper } from '../../lib/ajax/ajax-wrapper';
import { SimpleCache } from '../../qaamgo/helper/simple-cache';
import { JobInfoHelper } from '../uploader/core/info-helper/job-info-helper';

import { assertIsApiError } from '../../lib/test-and-assert/assert-api';
import {
    API_ERROR_CONNECTION_PROBLEM,
    API_ERROR_EXCEPTION,
    API_ERROR_JOB_NOT_FOUND,
    API_ERROR_LIMITS_INPUT_TOO_LARGE,
    API_ERROR_LIMITS_YOUTUBE_FORBIDDEN,
    API_ERROR_PAYMENT_REQUIRED,
    API_ERROR_TOO_MANY_RETRIES,
    API_ERROR_UNEXPECTED_JOB_STATUS,
    API_ERROR_UNKNOWN,
} from './consts';
import { INPUT_STATUS_API_CANCELED, INPUT_STATUS_API_FAILED } from '../uploader/core/consts';

var MAX_JOB_INFO_CACHING_TIME_SEC = 2;

/**
 * @param {Object} [config]
 *
 * @constructor
 */
function ApiConfig(config) {
    if (testIsUndefined(config)) {
        config = {};
    }

    this.baseApiData = null; // some data which is always send with each request

    if (testIsObject(config.baseApiData)) {
        this.baseApiData = config.baseApiData;
    }

    this.baseUrlApi = null; // can be set if data should be sent to a different domain

    if (testIsNonEmptyString(config.baseUrlApi)) {
        this.baseUrlApi = config.baseUrlApi;
    }

    this.paymentRequiredCallback = null; // a function which is called every time when we receive a 402

    if (testIsFunction(config.paymentRequiredCallback)) {
        this.paymentRequiredCallback = config.paymentRequiredCallback;
    }
}

export { ApiConfig };

/**
 * This is the standalone layer for all sort of API operations. All the methods which are
 * universal live in this class, all the project specific methods are in the sat-api-helper
 * and oc-api-helper class
 *
 * The config comes from these places:
 *
 * - OC:
 *   - base options are set in document_ready.tpl
 *   - this data ends up in qg-handle.js
 *   - this data ends up in qg-uploader-multifile.js in init()
 *
 * - Sat:
 *   - The main entry place for JS is _feature_base_scripts.html.twig
 *   - This includes this file _init_upload.html.twig
 *   - There is an object qgFeaturePage which is in feature-page.js
 *   - Another place of API usage is pdf-editor.js
 *
 * @param {string} type
 * @param {ApiConfig} [config]
 *
 * @constructor
 */
function Api(type, config) {
    assertIsNonEmptyString(type);

    this.apiHelper = new SatApiHelper(this, config);

    // Explode when someone passed the wrong type!
    assertIsNotNull(this.apiHelper, 'type must be "sat" or "oc"');

    this.ajaxWrapper = new AjaxWrapper();

    /** @type {SimpleCache} */
    this.jobCache = new SimpleCache(MAX_JOB_INFO_CACHING_TIME_SEC);

    // This is used to count the number of current getJobInfo request per job id
    // Note: this is a counter instead of a boolean flag to prevent weird corner cases
    //       when multiple getJobInfos are running
    this.numberOfCurrentJobInfoRequests = {};

    this.baseData = {};

    if (testIsObject(config.baseApiData)) {
        this.baseData = config.baseApiData;
    }

    this.baseAjaxOptions = {
        dataType: 'json',
        crossDomain: true,
        xhrFields: {
            withCredentials: true,
        },
        cache: false,
        maxRetries: 3,
        retryTimeout: true,
        retry400: false,
        retry500: true,
        retryOther: true,
    };
}

/**
 * Creates a job
 *
 * @param {Object} [jobData]
 *
 * @return {JQuery.Deferred<RawJobInfo>}
 */
Api.prototype.createJob = function (jobData) {
    var apiHelper = this.apiHelper;

    try {
        return apiHelper.createJob(jobData);
    } catch (e) {
        globalLogger.log('qgApiHelper exception', e);
    }

    var deferred = $.Deferred();

    deferred.reject(API_ERROR_EXCEPTION);

    return deferred;
};

/**
 * This creates a job with a check to the upload server
 *
 * If `checkConnectionToUploadServer` is true then the connection to the upload server is
 * checked. If it is not reachable, a few new job are created
 *
 * @param {Object} [jobData]
 * @param {number} [maxJobCreateRetries=1]
 *
 * @return {JQuery.Deferred<RawJobInfo, Boolean>}
 */
Api.prototype.createJobWithUploadServerCheck = function (jobData, maxJobCreateRetries) {
    if (!testIsNumber(maxJobCreateRetries)) {
        maxJobCreateRetries = 1;
    }

    var deferred = $.Deferred();

    /** @type Api */
    var _this = this;

    var currentJobCreateRetry = 0;

    function createJob() {
        _this.apiHelper
            .createJob(jobData)
            .done(function (rawJobInfo) {
                checkUploadServerHelper(rawJobInfo);
            })
            .fail(function (apiError, response) {
                assertIsApiError(apiError);

                deferred.reject(apiError, response);
            });
    }

    /**
     * @param {RawJobInfo} rawJobInfo
     */
    function checkUploadServerHelper(rawJobInfo) {
        _this
            .checkUploadServer(rawJobInfo.server)
            .done(function () {
                var uploadServerReachable = true;

                deferred.resolve(rawJobInfo, uploadServerReachable);
            })
            .fail(function (apiError, response) {
                if (apiError === API_ERROR_PAYMENT_REQUIRED) {
                    deferred.reject(API_ERROR_PAYMENT_REQUIRED, response);

                    return;
                }

                if (currentJobCreateRetry >= maxJobCreateRetries) {
                    globalLogger.log('qgApiHelper createJob info', 'no upload server found');

                    var uploadServerReachable = false;

                    deferred.resolve(rawJobInfo, uploadServerReachable);

                    return;
                }

                currentJobCreateRetry++;

                var maxTimeoutMs = 10000;

                var timeoutMs = currentJobCreateRetry * 1000;

                if (timeoutMs > maxTimeoutMs) {
                    timeoutMs = maxTimeoutMs;
                }

                setTimeout(function () {
                    createJob();
                }, timeoutMs);
            });
    }

    createJob();

    return deferred;
};

/**
 * Tests if a downloadserver is reachable (perhaps IP blocks or other problems)
 *
 * @param {string} serverUrl
 * @param {number} [maxRetries=0]
 *
 * @return {JQuery.Deferred<>}
 */
Api.prototype.checkUploadServer = function (serverUrl, maxRetries) {
    assertIsNonEmptyString(serverUrl);

    if (!testIsNumber(maxRetries)) {
        maxRetries = 0; // No retries by default!
    }

    var aliveEndpointUrl = serverUrl.replace(/(\/dl.*)/, '/dl/alive');

    var options = {
        url: aliveEndpointUrl,
        timeout: 10000,
        maxRetries: maxRetries,
    };

    var startTime_ms = Date.now();

    var deferred = $.Deferred();
    return deferred.resolve();
    this.ajaxWrapper
        .get(options)
        .done(function (response, textStatus, jqXHR) {
            deferred.resolve();
        })
        .fail(function (jqXHR, textStatus, errorThrown) {
            var uploadServer = serverUrl.match(/(www[^.]*)/)[0];

            var duration_s = (Date.now() - startTime_ms) / 1000;

            globalLogger.log('qgApiHelper checkUploadServer fail upload server not available', {
                upload_server: uploadServer,
                http_status: jqXHR.status,
                ajax_status: textStatus,
                ajax_error: errorThrown,
                duration_s: duration_s,
            });

            deferred.reject(API_ERROR_CONNECTION_PROBLEM);
        });

    return deferred;
};

/**
 * Get job info
 *
 * @param {string} jobId
 * @param async
 * @param sync
 *
 * @return {JQuery.Deferred<RawJobInfo>}
 */
Api.prototype.getJobInfo = function (jobId, async = false, sync = false) {
    assertIsUUID4(jobId);

    var apiHelper = this.apiHelper;

    try {
        return apiHelper.getJobInfo(jobId, async, sync);
    } catch (e) {
        globalLogger.log('qgApiHelper exception', e);
    }

    var deferred = $.Deferred();

    deferred.reject(API_ERROR_EXCEPTION);

    return deferred;
};

/**
 * @param {string} jobId
 * @param {string}  status
 * @param {number} [retries=5]
 * @param {Boolean}  [async=true]
 *
 * @returns {JQuery.Deferred<RawJobInfo>}
 */
Api.prototype.getJobInfoUntilStatus = function (jobId, status, retries, async) {
    assertIsUUID4(jobId);

    var apiHelper = this.apiHelper;

    try {
        return apiHelper.getJobInfoUntilStatus(jobId, status, retries, async);
    } catch (e) {
        globalLogger.log('qgApiHelper exception', e);
    }

    var deferred = $.Deferred();

    deferred.reject(API_ERROR_EXCEPTION);

    return deferred;
};

/**
 * Get job info
 *
 * @param {string} jobId
 * @param {number} [retries=5]
 *
 * @return {JQuery.Deferred<RawJobInfo>}
 */
Api.prototype.getJobInfoWithRetries = function (jobId, retries) {
    assertIsUUID4(jobId);

    if (!testIsNumber(retries)) {
        retries = 5;
    }

    assertIsNumber(retries);
    assertIsTrue(retries > 0);

    var apiHelper = this.apiHelper;

    try {
        return apiHelper.getJobInfoWithRetries(jobId, retries);
    } catch (e) {
        globalLogger.log('qgApiHelper exception', e);
    }

    var deferred = $.Deferred();

    deferred.reject(API_ERROR_EXCEPTION);

    return deferred;
};

/**
 * Get cached job info.
 *
 * Warning: only use this for jobs which:
 *
 * 1) were created by this instance of the Api
 * 2) already had a successful call to getJobInfo()
 *
 * Do NOT use getCachedJobInfo for jobs which are unknown to the current instance of API!
 * Your code WILL explode!
 *
 * @param {string} jobId
 *
 * @return {JQuery.Deferred<RawJobInfo>}
 */
Api.prototype.getCachedJobInfo = function (jobId) {
    assertIsUUID4(jobId);

    var cachedData = this.jobCache.getData(jobId);

    // There must be job data in the cache!
    assertIsNotNull(cachedData);

    // Just to make sure that this field really exists...
    if (!this.numberOfCurrentJobInfoRequests.hasOwnProperty(jobId)) {
        this.numberOfCurrentJobInfoRequests[jobId] = 0;
    }

    var areJobInfoRequestsRunning = this.numberOfCurrentJobInfoRequests[jobId] > 0;

    // If the data has been expired we need new data. But we only do that if noone else
    // is currently requesting data from the API. If there are active requests then we
    // return the old data, even if it has expired
    if (cachedData === false && !areJobInfoRequestsRunning) {
        return this.getJobInfo(jobId);
    }

    var jobInfo = this.jobCache.getDataEvenIfTooOld(jobId);

    var deferred = $.Deferred();

    deferred.resolve(jobInfo);

    return deferred;
};

// /**
//  * @param {string} jobId
//  * @param {object} inputData
//  *
//  * @return {*}
//  */
// Api.prototype.resetJobInfoCache = function(jobId) {
//     assertIsUUID4(jobId);
//     assertObjectHasKey(this.jobInfoCache, jobId);
//
//     // this 'hack' forces a cache refresh because the job data looks really old
//     this.jobInfoCache[jobId].time_ms = 0;
// };

/**
 * @param {string} jobId
 * @param {object} inputData
 *
 * @return {JQuery.Deferred<RawInputInfo>}
 */
Api.prototype.addInput = function (jobId, inputData) {
    assertIsUUID4(jobId);
    assertIsObject(inputData);

    var apiHelper = this.apiHelper;

    try {
        return apiHelper.addInput(jobId, inputData);
    } catch (e) {
        globalLogger.log('qgApiHelper exception', e);
    }

    var deferred = $.Deferred();

    deferred.reject(API_ERROR_EXCEPTION);

    return deferred;
};

/**
 * @param {string} jobId
 * @param {object} inputData
 * @param {number} numberOfInputsInJob
 *
 * @return {JQuery.Deferred<RawInputInfo>}
 */
Api.prototype.addInputWithCheck = function (jobId, inputData, numberOfInputsInJob) {
    assertIsUUID4(jobId);
    assertIsObject(inputData);
    assertIsNumber(numberOfInputsInJob);
    assertIsTrue(numberOfInputsInJob >= 0);

    var apiHelper = this.apiHelper;

    try {
        return apiHelper.addInputWithCheck(jobId, inputData, numberOfInputsInJob);
    } catch (e) {
        globalLogger.log('qgApiHelper exception', e);
    }

    var deferred = $.Deferred();

    deferred.reject(API_ERROR_EXCEPTION);

    return deferred;
};

/**
 * @param {string} jobId
 * @param {Array} inputs
 * @param {number} previousNumberOfInputsInJob
 *
 * @return {JQuery.Deferred}
 */
Api.prototype.addInputsWithCheck = function (jobId, inputs, previousNumberOfInputsInJob) {
    assertIsUUID4(jobId);
    assertIsArray(inputs);
    assertIsTrue(inputs.length > 0);
    assertIsNumber(previousNumberOfInputsInJob);
    assertIsTrue(previousNumberOfInputsInJob >= 0);

    var apiHelper = this.apiHelper;

    /** @type {Api} */
    var _this = this;

    var deferred = $.Deferred();

    var currentInput = 0;
    var numberOfInputs = inputs.length;
    var numberOfInputsInJob = previousNumberOfInputsInJob;

    function addInput() {
        _this
            .addInputWithCheck(jobId, inputs[currentInput], numberOfInputsInJob)
            .done(function () {
                currentInput++;
                numberOfInputsInJob++;

                if (currentInput >= numberOfInputs) {
                    deferred.resolve();

                    return;
                }

                // wait a tiny bit to not overload the server
                setTimeout(addInput, 200);
            })
            .fail(function (apiError, response) {
                globalLogger.log('qgApiHelper addInputsWithCheck fail', apiError);

                deferred.reject(apiError, response);
            });
    }

    addInput();

    return deferred;
};

/**
 * @param {string} jobId
 * @param {object} inputsData
 *
 * @return {JQuery.Deferred}
 */
Api.prototype.addInputs = function (jobId, inputsData) {
    assertIsUUID4(jobId);

    var apiHelper = this.apiHelper;

    try {
        return apiHelper.addInputs(jobId, inputsData);
    } catch (e) {
        globalLogger.log('qgApiHelper exception', e);
    }

    var deferred = $.Deferred();

    deferred.reject(API_ERROR_EXCEPTION);

    return deferred;
};

/**
 * @param {string} jobId
 * @param {string} inputId
 *
 * @return {JQuery.Deferred}
 */
Api.prototype.addInputId = function (jobId, inputId) {
    assertIsUUID4(jobId);
    assertIsUUID4(inputId);

    var input = {
        type: 'input_id',
        source: inputId,
    };

    return this.addInput(jobId, input);
};

/**
 * @param {string} jobId
 * @param {string} externalUrl
 *
 * @param  {string} [engine='auto']
 * @return {JQuery.Deferred}
 */
Api.prototype.addExternalUrl = function (jobId, externalUrl, engine) {
    assertIsUUID4(jobId);
    assertIsNonEmptyString(externalUrl);

    if (testIsUndefined(engine)) {
        engine = 'auto';
    }

    var input = {
        type: 'remote',
        source: externalUrl,
        engine: engine,
    };

    return this.addInput(jobId, input);
};

/**
 * @param {string} jobId
 * @param {object} conversion
 *
 * @return {JQuery.Deferred}
 */
Api.prototype.addConversion = function (jobId, conversion) {
    assertIsUUID4(jobId);

    var apiHelper = this.apiHelper;

    try {
        return apiHelper.addConversion(jobId, conversion);
    } catch (e) {
        globalLogger.log('qgApiHelper exception', e);
    }

    var deferred = $.Deferred();

    deferred.reject(API_ERROR_EXCEPTION);

    return deferred;
};

/**
 * @param {string} jobId
 * @param {object} conversion
 *
 * @return {JQuery.Deferred}
 */
Api.prototype.addConversionWithCheck = function (jobId, conversion) {
    assertIsUUID4(jobId);

    var apiHelper = this.apiHelper;

    try {
        return apiHelper.addConversionWithCheck(jobId, conversion);
    } catch (e) {
        globalLogger.log('qgApiHelper exception', e);
    }

    var deferred = $.Deferred();

    deferred.reject(API_ERROR_EXCEPTION);

    return deferred;
};

/**
 * @param {String} jobId
 * @param {String} serializedForm
 *
 * @return {JQuery.Deferred}
 */
Api.prototype.startJob = function (jobId, serializedForm) {
    assertIsUUID4(jobId);

    var apiHelper = this.apiHelper;

    try {
        return apiHelper.startJob(jobId, serializedForm);
    } catch (e) {
        globalLogger.log('qgApiHelper exception', e);
    }

    var deferred = $.Deferred();

    deferred.reject(API_ERROR_EXCEPTION);

    return deferred;
};

/**
 * @param {string} jobId
 * @param {string} inputId
 *
 * @return {JQuery.Deferred}
 */
Api.prototype.deleteInput = function (jobId, inputId) {
    assertIsUUID4(jobId);
    assertIsUUID4(inputId);

    var apiHelper = this.apiHelper;

    try {
        return apiHelper.deleteInput(jobId, inputId);
    } catch (e) {
        globalLogger.log('qgApiHelper exception', e);
    }

    var deferred = $.Deferred();

    deferred.reject(API_ERROR_EXCEPTION);

    return deferred;
};

/**
 * @param {string} jobId
 * @param {string} inputId
 *
 * @return {JQuery.Deferred}
 */
Api.prototype.cancelJob = function (jobId) {
    assertIsUUID4(jobId);

    var apiHelper = this.apiHelper;

    try {
        return apiHelper.cancelJob(jobId);
    } catch (e) {
        globalLogger.log('qgApiHelper exception', e);
    }

    var deferred = $.Deferred();

    deferred.reject(API_ERROR_EXCEPTION);

    return deferred;
};

/**
 * @param {string} jobId
 * @param {string} inputId
 *
 * @return {JQuery.Deferred}
 */
Api.prototype.deleteInputAndWait = function (jobId, inputId) {
    assertIsUUID4(jobId);
    assertIsUUID4(inputId);

    var apiHelper = this.apiHelper;

    try {
        return apiHelper.deleteInputAndWait(jobId, inputId);
    } catch (e) {
        globalLogger.log('qgApiHelper exception', e);
    }

    var deferred = $.Deferred();

    deferred.reject(API_ERROR_EXCEPTION);

    return deferred;
};

/**
 * @param {string} jobId
 * @param {string} inputId
 * @param {string} password
 *
 * @return {JQuery.Deferred}
 */
Api.prototype.setPassword = function (jobId, inputId, password) {
    assertIsUUID4(jobId);
    assertIsUUID4(inputId);
    assertIsString(password);

    var apiHelper = this.apiHelper;

    try {
        return apiHelper.setPassword(jobId, inputId, password);
    } catch (e) {
        globalLogger.log('qgApiHelper exception', e);
    }

    var deferred = $.Deferred();
    deferred.reject(API_ERROR_EXCEPTION);

    return deferred;
};

/**
 * @param {string} jobId
 * @param {int} inputCount
 *
 * @return {JQuery.Deferred}
 */
Api.prototype.getJobInfoUntilInputCount = function (jobId, inputCount) {
    assertIsUUID4(jobId);

    var apiHelper = this.apiHelper;

    try {
        return apiHelper.getJobInfoUntilInputCount(jobId, inputCount);
    } catch (e) {
        globalLogger.log('qgApiHelper exception', e);
    }

    var deferred = $.Deferred();
    deferred.reject(API_ERROR_EXCEPTION);

    return deferred;
};

/**
 * @param {string} jobId
 *
 * @return {JQuery.Deferred}
 */
Api.prototype.getJobInfoUntilAtLeastOneInput = function (jobId) {
    assertIsUUID4(jobId);

    var apiHelper = this.apiHelper;

    try {
        return apiHelper.getJobInfoUntilAtLeastOneInput(jobId);
    } catch (e) {
        globalLogger.log('qgApiHelper exception', e);
    }

    var deferred = $.Deferred();
    deferred.reject(API_ERROR_EXCEPTION);

    return deferred;
};

/**
 * @param {string} jobId
 * @param {string} inputId
 *
 * @return {JQuery.Deferred}
 */
Api.prototype.waitForRemoteInput = function (jobId, inputId) {
    assertIsUUID4(jobId);
    assertIsUUID4(inputId);

    /** @type {Api} this is only done for better type hinting supprot */
    var _this = this;

    var deferred = $.Deferred();

    var startMs = Date.now();

    function retry() {
        var secondsSinceStart = (Date.now() - startMs) / 1000.0;

        var timeoutMs = 5000;

        if (secondsSinceStart < 10) {
            timeoutMs = 1000;
        } else if (secondsSinceStart < 30) {
            timeoutMs = 2000;
        } else if (secondsSinceStart < 60) {
            timeoutMs = 3000;
        }

        setTimeout(tryToGetJobInfo, timeoutMs);
    }

    // this is a helper function which makes it easier to deal with the different
    // sources of errors below
    function handleInputFail(errorData, jobInfoRaw, secondsSinceStart) {
        globalLogger.log('qgApiHelper waitForRemoteInput fail', {
            info: JSON.stringify(errorData),
            input_id: inputId,
            job_id: jobId,
            job_info: JSON.stringify(jobInfoRaw),
            duration_s: secondsSinceStart,
        });

        var apiError = API_ERROR_UNKNOWN;

        // TODO: there will/might be a code '4' for for 'too many files'. This should be
        //       supported although it should never happen if the logic in the uplaoder
        //       does not have any bugs

        if (errorData.code === 50 || errorData.code === 34) {
            // TODO: this might also be a JOB_TOO_LARGE ? pls investigate
            apiError = API_ERROR_LIMITS_INPUT_TOO_LARGE;
        } else if (errorData.code === 10) {
            apiError = API_ERROR_LIMITS_YOUTUBE_FORBIDDEN;
        }

        assertIsApiError(apiError);

        return apiError;
    }

    function handleGetJobInfo(jobInfoRaw) {
        var secondsSinceStart = (Date.now() - startMs) / 1000.0;

        // parse jobInfo to make it easier to use
        var jobInfoHelper = new JobInfoHelper(jobInfoRaw);

        var rawInputInfo = jobInfoHelper.getInput(inputId);

        // If the job info does not have the input info then we try again
        if (rawInputInfo === null) {
            // it might take a few seconds until the input shows up in thejob info. Therefore
            // the log is only sent if the input is still not there after a bit of waiting
            if (secondsSinceStart > 10) {
                globalLogger.log('qgApiHelper waitForRemoteInput info', {
                    info: 'input id missing from job',
                    input_id: inputId,
                    job_id: jobId,
                    job_info: JSON.stringify(jobInfoRaw),
                    duration_s: secondsSinceStart,
                });
            }

            retry();

            return;
        }

        if (jobInfoHelper.isInputDownloading(inputId)) {
            retry();

            return;
        }

        if (jobInfoHelper.isInputReady(inputId)) {
            deferred.resolve(rawInputInfo);

            globalLogger.log('qgApiHelper waitForRemoteInput done', {
                input_id: inputId,
                job_id: jobId,
                duration_s: secondsSinceStart,
            });

            return;
        }

        // TODO: T3506 refactor the code below when satcore support status for inputs.
        //       what to refactor: check status of input. if it is done then resolve.
        //       if it is failed then look up a reason in 'warning' or 'error' (if it exists)
        //       and reject. if it is still downloading then retry later

        var errorData = {
            code: 0,
            message: 'should never happen',
        };

        var inputError = jobInfoHelper.getInputError(inputId);
        var inputWarning = jobInfoHelper.getInputWarning(inputId);

        // Handle different ways to check if a remote has failed
        if (inputError !== null) {
            errorData = inputError;
        } else if (inputWarning !== null) {
            errorData = inputWarning;
        } else if (rawInputInfo.status === INPUT_STATUS_API_FAILED) {
            // If everything is implemented correctly then a failed input should have been
            // detected above. But if there is a bug where 'error' or 'warning' is missing
            // from the job info (e.g. T3500) then we directly check for the status of the input.
            //
            // Directly checking the input status has the disadvantage that we only
            // see that the input failed but not why. Therefore checking the errors and
            // warnings of the job is the preferred way of doing this.
            //
            // If we end up here too often then this needs investigation and fixing!

            errorData = {
                code: 0,
                message: 'server response is missing input warnings and input errrors',
            };
        } else if (rawInputInfo.status === INPUT_STATUS_API_CANCELED) {
            // TODO: The functionality behind the 'canceled' input status is currently not implemented in the API
            //       and therefore should never happen. We still handle it in case it ever gets activated and
            //       noone checks this code
            errorData = {
                code: 0,
                message: 'input was canceled',
            };
        }

        var apiError = handleInputFail(errorData, jobInfoRaw, secondsSinceStart);

        assertIsApiError(apiError);

        deferred.reject(apiError);
    }

    function tryToGetJobInfo() {
        _this
            .getCachedJobInfo(jobId)
            .done(function (jobInfoRaw) {
                assertIsRawJobInfo(jobInfoRaw);
                handleGetJobInfo(jobInfoRaw);
            })
            .fail(function (apiError, response) {
                assertIsApiError(apiError);

                if (apiError === API_ERROR_PAYMENT_REQUIRED) {
                    deferred.reject(apiError, response);

                    return;
                }

                globalLogger.log('qgApiHelper waitForRemoteInput retry', {
                    info: 'error: ' + apiError,
                    job_id: jobId,
                    input_id: inputId,
                });

                retry();
            });
    }

    tryToGetJobInfo();

    return deferred;
};

/**
 * @param {object} conversionOptions
 *
 * @return {JQuery.Deferred}
 */
Api.prototype.savePreset = function (conversionOptions) {
    var apiHelper = this.apiHelper;

    try {
        return apiHelper.savePreset(conversionOptions);
    } catch (e) {
        globalLogger.log('qgApiHelper exception', e);
    }

    var deferred = $.Deferred();
    deferred.reject(API_ERROR_EXCEPTION);

    return deferred;
};

/**
 * @param {string} jobId
 *
 * @return {JQuery.Deferred<RawJobInfo>}
 */
Api.prototype.waitForCompletedJob = function (jobId) {
    var deferred = $.Deferred();
    var start_ms = Date.now();

    var failRetries = 0;

    /**
     * @param {number} timeSinceStart_ms
     *
     * @return {number}
     */
    function getTimeout_ms(timeSinceStart_ms) {
        var timeout_ms = 500;

        if (timeSinceStart_ms > 2000) {
            timeout_ms = 1000;
        }

        if (timeSinceStart_ms > 5000) {
            timeout_ms = 2000;
        }

        if (timeSinceStart_ms > 10000) {
            timeout_ms = 3000;
        }

        return timeout_ms;
    }

    /** @type {Api} */
    var _this = this;

    function waitForCompletedJobHelper() {
        if (failRetries > 5) {
            deferred.reject(API_ERROR_TOO_MANY_RETRIES);

            return;
        }

        _this
            .getJobInfoWithRetries(jobId, 3)
            .done(function (jobInfo) {
                var timeSinceStart_ms = Date.now() - start_ms;

                var status = jobInfo.status.code;

                if (status === 'completed') {
                    deferred.resolve(jobInfo);

                    return;
                }

                if (status === 'failed') {
                    globalLogger.log('qgApiHelper waitForCompletedJob - job is failed', { job_id: jobId });

                    deferred.reject(API_ERROR_UNEXPECTED_JOB_STATUS);

                    return;
                }

                // If the job is 'incomplete' or 'ready' then we wait a few seconds until
                // we reject
                if ((status === 'incomplete' || status === 'ready') && timeSinceStart_ms > 4000) {
                    globalLogger.log('qgApiHelper waitForCompletedJob - job has unextected status', {
                        job_id: jobId,
                        info: 'status: ' + status,
                    });

                    deferred.reject(API_ERROR_UNEXPECTED_JOB_STATUS);

                    return;
                }

                var timeout_ms = getTimeout_ms(timeSinceStart_ms);

                setTimeout(waitForCompletedJobHelper, timeout_ms);
            })
            .fail(function (apiError, response) {
                if (apiError === API_ERROR_PAYMENT_REQUIRED) {
                    deferred.reject(API_ERROR_PAYMENT_REQUIRED, response);

                    return;
                }

                failRetries++;

                setTimeout(waitForCompletedJobHelper, 5000);
            });
    }

    waitForCompletedJobHelper();

    return deferred;
};

/**
 * @param {Object} jobData
 * @param {Object} conversionData
 *
 * @return {JQuery.Deferred<RawJobInfo>}
 */
Api.prototype.createJobAndConversion = function (jobData, conversionData) {
    var deferred = $.Deferred();

    /** @type {Api} */
    const _this = this;

    this.createJob(jobData)
        .done(function (job) {
            _this
                .addConversionWithCheck(job.id, conversionData)
                .done(function (conversion) {
                    // NOTE: due to the async nature the sat-client the data in conversion will
                    // most likely be empty. therefore we return just the job with an empty conversion
                    deferred.resolve(job);
                })
                .fail(function (apiError, response) {
                    if (apiError === API_ERROR_PAYMENT_REQUIRED) {
                        deferred.reject(API_ERROR_PAYMENT_REQUIRED, response);

                        return;
                    }

                    deferred.reject(API_ERROR_UNKNOWN, response);
                });
        })
        .fail(function (apiError, response) {
            if (apiError === API_ERROR_PAYMENT_REQUIRED) {
                deferred.reject(API_ERROR_PAYMENT_REQUIRED, response);

                return;
            }

            deferred.reject(API_ERROR_UNKNOWN, response);
        });

    return deferred;
};

/**
 * @param {String} inputId
 * @param {Number} [width=200]
 *
 * @return {JQuery.Deferred<String>}
 */
Api.prototype.createThumbnailFromInputId = function (inputId, width) {
    var deferred = $.Deferred();

    if (testIsUndefined(width)) {
        width = 200;
    }

    const jobData = {
        operation: 'thumbnail system',
    };

    const conversionData = {
        category: 'operation',
        target: 'thumbnail',
        options: {
            thumbnail_target: 'jpg',
            width: width,
            allow_multiple_outputs: true,
            pages: '1',
        },
    };

    const _this = this;

    this.createJobAndConversion(jobData, conversionData)
        .done(function (job) {
            _this
                .addInputId(job.id, inputId)
                .done(function (data) {
                    _this
                        .waitForCompletedJob(job.id)
                        .done(function (job) {
                            deferred.resolve(job.output[0].uri);
                        })
                        .fail(function (apiError, response) {
                            if (apiError === API_ERROR_PAYMENT_REQUIRED) {
                                deferred.reject(API_ERROR_PAYMENT_REQUIRED, response);

                                return;
                            }

                            deferred.reject(API_ERROR_UNKNOWN);
                        });
                })
                .fail(function (apiError, response) {
                    if (apiError === API_ERROR_PAYMENT_REQUIRED) {
                        deferred.reject(API_ERROR_PAYMENT_REQUIRED, response);

                        return;
                    }

                    deferred.reject(API_ERROR_UNKNOWN);
                });
        })
        .fail(function (apiError, response) {
            if (apiError === API_ERROR_PAYMENT_REQUIRED) {
                deferred.reject(API_ERROR_PAYMENT_REQUIRED, response);

                return;
            }

            deferred.reject(API_ERROR_UNKNOWN);
        });

    return deferred;
};

Api.prototype.createJobPromise = async function (jobData) {
    return new Promise(async (resolve, reject) => {
        var apiHelper = this.apiHelper;

        try {
            const job = await apiHelper.createJobPromise(jobData);
            resolve(job);
        } catch (e) {
            globalLogger.log('qgApiHelper exception', e);
            reject(e);
        }
    });
};

/**
 * @param {string} jobId
 * @param {object} conversion
 *
 * @return {JQuery.Deferred}
 */
Api.prototype.addConversionWithCheckPromise = async function (jobId, conversion) {
    return new Promise((resolve, reject) => {
        assertIsUUID4(jobId);

        var apiHelper = this.apiHelper;

        try {
            apiHelper.addConversionWithCheckPromise(jobId, conversion);
            resolve();
        } catch (e) {
            globalLogger.log('qgApiHelper exception', e);
            reject(API_ERROR_EXCEPTION);
        }
    });
};

Api.prototype.uploadBase64Promise = async function (base64UploadUrl, data, token) {
    return new Promise(async (resolve, reject) => {
        var apiHelper = this.apiHelper;

        try {
            const job = await apiHelper.uploadBase64Promise(base64UploadUrl, data, token);
            resolve(job);
        } catch (e) {
            reject(API_ERROR_EXCEPTION);
            globalLogger.log('qgApiHelper exception', e);
        }
    });
};

Api.prototype.getJobInfoUntilStatusPromise = async function (jobId, status, retries, async) {
    return new Promise(async (resolve, reject) => {
        var apiHelper = this.apiHelper;

        try {
            const job = await apiHelper.getJobInfoUntilStatusPromise(jobId, status, retries, async);
            resolve(job);
        } catch (e) {
            if (e?.message === API_ERROR_JOB_NOT_FOUND) {
                reject(API_ERROR_JOB_NOT_FOUND);
            }

            reject(API_ERROR_EXCEPTION);
            globalLogger.log('qgApiHelper exception', e);
        }
    });
};

/**
 * Create a new sat Api
 *
 * @param {ApiConfig} config
 *
 * @return {Api}
 */
var createSatApi = function (config) {
    assertIsObject(config);

    return new Api('sat', config);
};

export { createSatApi };

// do not use this directly! This is just exported for type-hinting/auto-completion
export { Api };
